import debounce from 'lodash/debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { Head } from '@/components/Head';
import { LocationMap } from '@/components/LocationMap';
import { useCurrency } from '@/providers/CurrencyProvider';
import storage from '@/utils/storage';

import { PropertyFilters } from '../api';
import { Header } from '../components/Header';
import { useGetAvailableProperties } from '../hooks';

export const Market = () => {
  const token = storage.getToken();
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [filters, setFilters] = useState<PropertyFilters>({});
  const [debouncedFilters, setDebouncedFilters] = useState<PropertyFilters>({});
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const loggedInUser = storage.getUser();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  token && loggedInUser && navigate('/portal/real-estate');

  // A new search should start back at page 1 — staying on, say, page 3 of
  // the old unfiltered list would silently show "no results" instead of the
  // matches that do exist.
  useEffect(() => {
    setPage(1);
  }, [debouncedFilters]);

  const { data, isLoading } = useGetAvailableProperties({
    ...debouncedFilters,
    page,
    pageSize,
  });
  const properties = data?.properties ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));

  const hasFilters = Object.values(filters).some(Boolean);

  const applyDebounced = useRef(
    debounce((f: PropertyFilters) => setDebouncedFilters(f), 400),
  ).current;

  const handleChange = useCallback(
    (field: keyof PropertyFilters, value: string) => {
      const next = { ...filters, [field]: value };
      setFilters(next);
      applyDebounced(next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters],
  );

  const handleClear = () => {
    setFilters({});
    setDebouncedFilters({});
    applyDebounced.cancel();
  };

  const inputClass =
    'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';

  return (
    <div>
      {token && loggedInUser && navigate('/portal/real-estate')}
      <Head title='Market' />
      <Header />

      {/* Filters */}
      <div className='flex flex-wrap gap-3 px-4 py-4 bg-gray-50 border-b'>
        <input
          type='text'
          placeholder='Search by name…'
          value={filters.name ?? ''}
          onChange={(e) => handleChange('name', e.target.value)}
          className={`flex-1 min-w-[160px] ${inputClass}`}
        />
        <input
          type='text'
          placeholder='Search by location…'
          value={filters.location ?? ''}
          onChange={(e) => handleChange('location', e.target.value)}
          className={`flex-1 min-w-[160px] ${inputClass}`}
        />
        <input
          type='number'
          placeholder='Min price'
          value={filters.minPrice ?? ''}
          onChange={(e) => handleChange('minPrice', e.target.value)}
          className={`w-[120px] ${inputClass}`}
        />
        <input
          type='number'
          placeholder='Max price'
          value={filters.maxPrice ?? ''}
          onChange={(e) => handleChange('maxPrice', e.target.value)}
          className={`w-[120px] ${inputClass}`}
        />
        {hasFilters && (
          <button
            type='button'
            onClick={handleClear}
            className='ml-auto px-4 py-2 text-sm text-gray-500 border border-gray-300 rounded-lg hover:text-gray-800'
          >
            Clear
          </button>
        )}
      </div>

      {isLoading ? (
        <>loading</>
      ) : !properties.length ? (
        <p className='text-center text-gray-400 py-16 text-sm'>
          No properties match your filters.
        </p>
      ) : (
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 p-4'>
          {properties.map((item: any) => (
            <div
              key={item._id}
              className='bg-white shadow-md rounded-2xl overflow-hidden hover:shadow-lg transition-shadow duration-300'
            >
              <img
                src={item.image}
                alt={item.name}
                className='w-full h-40 object-cover'
              />

              <div className='p-4 space-y-2'>
                <div className='flex items-center justify-between'>
                  <h2 className='text-lg font-semibold text-gray-800'>
                    {item.name}
                  </h2>

                  <button
                    type='button'
                    className='text-sm font-medium text-blue-600 hover:underline'
                    onClick={() => setSelectedItem(item)}
                  >
                    View
                  </button>
                </div>

                <p className='text-gray-500 text-sm'>{item.location}</p>

                <p className='text-gray-700 text-sm'>Size: {item.size}</p>
                <p className='text-gray-700 text-sm'>
                  Quantity: {item.quantity}
                </p>
                <p className='text-gray-700 text-sm'>Source: {item.source}</p>

                <div className='flex items-center justify-between pt-2'>
                  <span className='text-xl font-bold text-blue-600'>
                    {formatCurrency(item.price)}
                  </span>
                  <button
                    type='button'
                    onClick={() => {
                      token && loggedInUser
                        ? navigate('/portal/real-estate')
                        : navigate('/auth/signin');
                    }}
                    className={`text-sm font-medium px-2 py-1 rounded-full ${
                      item.isAvailable
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {'Buy'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && totalPages > 1 && (
        <div className='flex items-center justify-center gap-4 pb-8'>
          <button
            type='button'
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className='px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-40'
          >
            Previous
          </button>
          <span className='text-sm text-gray-500'>
            Page {page} of {totalPages}
          </span>
          <button
            type='button'
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className='px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-40'
          >
            Next
          </button>
        </div>
      )}

      {selectedItem && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
          <div className='bg-white w-full max-w-2xl rounded-2xl shadow-xl relative flex flex-col max-h-[90vh]'>
            <button
              type='button'
              onClick={() => setSelectedItem(null)}
              className='absolute top-3 right-3 z-10 text-gray-400 hover:text-gray-700'
            >
              ✕
            </button>

            <div className='overflow-y-auto flex-1 p-6'>
              <img
                src={selectedItem.image}
                alt={selectedItem.name}
                className='w-full h-48 object-cover rounded-xl mb-4'
              />

              <h2 className='text-2xl font-bold text-gray-800'>
                {selectedItem.name}
              </h2>
              <p className='text-gray-500 mt-1'>{selectedItem.location}</p>

              <LocationMap location={selectedItem.location} />

              <div className='mt-4 space-y-2 text-sm text-gray-700'>
                <p>
                  <strong>Size:</strong> {selectedItem.size}
                </p>
                <p>
                  <strong>Quantity:</strong> {selectedItem.quantity}
                </p>
                <p>
                  <strong>Source:</strong> {selectedItem.source}
                </p>

                <div>
                  <h3 className='font-semibold text-gray-800 mb-1'>
                    Description
                  </h3>
                  <p className='leading-relaxed whitespace-pre-line'>
                    {selectedItem.description}
                  </p>
                </div>
              </div>
            </div>

            <div className='flex-shrink-0 flex justify-between items-center border-t px-6 py-4'>
              <span className='text-xl font-bold text-blue-600'>
                {formatCurrency(selectedItem.price)}
              </span>

              <button
                type='button'
                onClick={() => {
                  token && loggedInUser
                    ? navigate('/portal/real-estate')
                    : navigate('/auth/signin');
                }}
                className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'
              >
                Buy
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};
